import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Copy,
  Trash2,
  History as HistoryIcon,
  Image as ImageIcon,
  Facebook,
  Instagram,
  Megaphone,
  Sparkles,
  Mic,
  ListChecks,
  Bookmark,
  BookmarkCheck,
  CheckCircle2,
  Clock,
} from "lucide-react";

const PLATFORM_META = {
  facebook: { label: "Facebook", icon: Facebook },
  x: { label: "X", icon: Megaphone },
  instagram: { label: "Instagram", icon: Instagram },
};

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtMinutes(n) {
  return n === 1 ? "1 min" : `${n} min`;
}

export default function History() {
  const [posts, setPosts] = useState([]);
  const [speeches, setSpeeches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedPosts, setExpandedPosts] = useState({});
  const [expandedSpeeches, setExpandedSpeeches] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        api.get("/posts/history"),
        api.get("/speeches/history"),
      ]);
      setPosts(p.data || []);
      setSpeeches(s.data || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't copy. Select the text manually.");
    }
  };

  const removePost = async (id) => {
    try {
      await api.delete(`/posts/history/${id}`);
      setPosts((prev) => prev.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const patchDraft = async (itemId, platform, body) => {
    const { data } = await api.patch(`/posts/history/${itemId}/drafts/${platform}`, body);
    setPosts((prev) =>
      prev.map((item) =>
        item.id !== itemId
          ? item
          : {
              ...item,
              drafts: item.drafts.map((d) =>
                d.platform === platform ? { ...d, ...data } : d
              ),
            }
      )
    );
    return data;
  };

  const toggleSaved = async (item, draft) => {
    try {
      await patchDraft(item.id, draft.platform, { saved: !draft.saved });
      toast.success(draft.saved ? "Removed from Saved Posts" : "Saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const toggleUsed = async (item, draft) => {
    try {
      await patchDraft(item.id, draft.platform, { used: !draft.used });
      toast.success(draft.used ? "Moved back to queue" : "Marked as used");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const removeSpeech = async (id) => {
    try {
      await api.delete(`/speeches/history/${id}`);
      setSpeeches((prev) => prev.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wider text-flag-red">History</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-flag-navy mt-1">Everything you've drafted</h1>
          <p className="text-slate-600 mt-2">Posts and speeches are saved here automatically.</p>
        </div>
      </div>

      {loading && <div className="mt-10 text-slate-500" data-testid="history-loading">Loading…</div>}
      {error && <div className="mt-6 text-sm text-flag-red bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}

      <Tabs defaultValue="posts" className="mt-8">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="posts" data-testid="history-tab-posts">
            <Sparkles className="h-4 w-4 mr-1.5" /> Posts ({posts.length})
          </TabsTrigger>
          <TabsTrigger value="speeches" data-testid="history-tab-speeches">
            <Mic className="h-4 w-4 mr-1.5" /> Speeches ({speeches.length})
          </TabsTrigger>
        </TabsList>

        {/* Posts */}
        <TabsContent value="posts" className="mt-6">
          {!loading && posts.length === 0 && (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center" data-testid="history-posts-empty">
              <HistoryIcon className="h-8 w-8 text-slate-400 mx-auto" />
              <h2 className="font-display text-xl font-bold text-flag-navy mt-3">No post drafts yet</h2>
              <p className="text-slate-600 mt-1">Generate your first post and it will land here automatically.</p>
              <Link to="/generator" data-testid="history-posts-empty-cta">
                <Button className="mt-5 h-11 px-5 bg-flag-red hover:bg-flag-red-hover text-white font-bold">Open Post Generator</Button>
              </Link>
            </div>
          )}
          <ul className="space-y-4">
            {posts.map((item) => {
              const isOpen = !!expandedPosts[item.id];
              return (
                <li key={item.id} className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid={`history-post-item-${item.id}`}>
                  <button
                    type="button"
                    onClick={() => setExpandedPosts((s) => ({ ...s, [item.id]: !isOpen }))}
                    data-testid={`history-post-toggle-${item.id}`}
                    className="w-full text-left p-5 sm:p-6 flex items-start justify-between gap-4 hover:bg-slate-50 rounded-xl transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-2 flex-wrap">
                        {formatDate(item.created_at)}
                        {item.has_image && (
                          <span className="inline-flex items-center gap-1 text-flag-navy">
                            <ImageIcon className="h-3 w-3" /> photo
                          </span>
                        )}
                        {item.drafts.some((d) => d.saved) && (
                          <span
                            data-testid={`history-post-has-saved-${item.id}`}
                            className="inline-flex items-center gap-1 text-flag-red"
                          >
                            <BookmarkCheck className="h-3 w-3" /> saved
                          </span>
                        )}
                      </div>
                      <p className="mt-2 text-slate-800 line-clamp-2">{item.moment}</p>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {item.drafts.map((d) => {
                          const meta = PLATFORM_META[d.platform] || PLATFORM_META.facebook;
                          return (
                            <span key={`${item.id}-pill-${d.platform}`} className="text-xs px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-semibold">{meta.label}</span>
                          );
                        })}
                      </div>
                    </div>
                    <div className="text-flag-navy text-sm font-bold flex-shrink-0">{isOpen ? "Hide" : "Open"}</div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-slate-100 p-5 sm:p-6 space-y-4">
                      {item.drafts.map((d) => {
                        const meta = PLATFORM_META[d.platform] || PLATFORM_META.facebook;
                        const Icon = meta.icon;
                        return (
                          <div key={`${item.id}-${d.platform}`} className="border border-slate-200 rounded-lg p-4" data-testid={`history-post-draft-${item.id}-${d.platform}`}>
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <div className="flex items-center gap-2">
                                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-flag-navy/5 text-flag-navy">
                                  <Icon className="h-3.5 w-3.5" />
                                </span>
                                <span className="font-display font-bold text-flag-navy text-sm">{meta.label}</span>
                                {d.saved && (
                                  <span
                                    data-testid={`history-post-status-${item.id}-${d.platform}`}
                                    className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${d.used ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-900"}`}
                                  >
                                    {d.used ? (
                                      <><CheckCircle2 className="h-3 w-3" /> Used</>
                                    ) : (
                                      <><Clock className="h-3 w-3" /> Queued</>
                                    )}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Button
                                  variant={d.saved ? "secondary" : "outline"}
                                  onClick={() => toggleSaved(item, d)}
                                  data-testid={`history-post-save-${item.id}-${d.platform}`}
                                  className={`h-8 text-xs font-semibold ${d.saved ? "bg-flag-red text-white hover:bg-flag-red-hover" : "border-2"}`}
                                >
                                  {d.saved ? (
                                    <>
                                      <BookmarkCheck className="h-3 w-3 mr-1" /> Saved
                                    </>
                                  ) : (
                                    <>
                                      <Bookmark className="h-3 w-3 mr-1" /> Save
                                    </>
                                  )}
                                </Button>
                                {d.saved && (
                                  <Button
                                    variant="outline"
                                    onClick={() => toggleUsed(item, d)}
                                    data-testid={`history-post-used-${item.id}-${d.platform}`}
                                    className="h-8 text-xs font-semibold border-2"
                                  >
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    {d.used ? "Mark as queued" : "Mark as used"}
                                  </Button>
                                )}
                                <Button variant="outline" onClick={() => copy(d.text)} data-testid={`history-post-copy-${item.id}-${d.platform}`} className="h-8 border-2 text-xs font-semibold">
                                  <Copy className="h-3 w-3 mr-1" /> Copy
                                </Button>
                              </div>
                            </div>
                            <p className="mt-3 text-slate-800 whitespace-pre-wrap leading-relaxed">{d.text}</p>
                          </div>
                        );
                      })}
                      <div className="flex justify-end pt-2">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" data-testid={`history-post-delete-${item.id}`} className="h-9 text-flag-red hover:text-flag-red-hover hover:bg-red-50 font-semibold">
                              <Trash2 className="h-4 w-4 mr-1.5" /> Delete
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this draft set?</AlertDialogTitle>
                              <AlertDialogDescription>This will permanently remove all three drafts from your history.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel data-testid={`history-post-delete-cancel-${item.id}`}>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => removePost(item.id)} data-testid={`history-post-delete-confirm-${item.id}`} className="bg-flag-red hover:bg-flag-red-hover">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </TabsContent>

        {/* Speeches */}
        <TabsContent value="speeches" className="mt-6">
          {!loading && speeches.length === 0 && (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center" data-testid="history-speeches-empty">
              <Mic className="h-8 w-8 text-slate-400 mx-auto" />
              <h2 className="font-display text-xl font-bold text-flag-navy mt-3">No speeches yet</h2>
              <p className="text-slate-600 mt-1">Write your first speech or talking points and it will land here.</p>
              <Link to="/speeches" data-testid="history-speeches-empty-cta">
                <Button className="mt-5 h-11 px-5 bg-flag-red hover:bg-flag-red-hover text-white font-bold">Open Speech Writer</Button>
              </Link>
            </div>
          )}
          <ul className="space-y-4">
            {speeches.map((item) => {
              const isOpen = !!expandedSpeeches[item.id];
              const isFullSpeech = item.output_type === "full_speech";
              const Icon = isFullSpeech ? Mic : ListChecks;
              return (
                <li key={item.id} className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid={`history-speech-item-${item.id}`}>
                  <button
                    type="button"
                    onClick={() => setExpandedSpeeches((s) => ({ ...s, [item.id]: !isOpen }))}
                    data-testid={`history-speech-toggle-${item.id}`}
                    className="w-full text-left p-5 sm:p-6 flex items-start justify-between gap-4 hover:bg-slate-50 rounded-xl transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{formatDate(item.created_at)}</div>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded bg-flag-red/10 text-flag-red">
                          <Icon className="h-3.5 w-3.5" />
                        </span>
                        <span className="font-display font-bold text-flag-navy">
                          {isFullSpeech ? "Full Speech" : "Talking Points"}
                        </span>
                        <span className="text-xs text-slate-500">· {fmtMinutes(item.length_minutes)} · {item.word_count} words</span>
                      </div>
                      <p className="mt-2 text-slate-800 line-clamp-2">{item.topic}</p>
                    </div>
                    <div className="text-flag-navy text-sm font-bold flex-shrink-0">{isOpen ? "Hide" : "Open"}</div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-slate-100 p-5 sm:p-6 space-y-4">
                      {item.background && (
                        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60">
                          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Your context</div>
                          <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{item.background}</p>
                        </div>
                      )}
                      <div className="border border-slate-200 rounded-lg p-4" data-testid={`history-speech-content-${item.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-display font-bold text-flag-navy text-sm">
                            {isFullSpeech ? "Full Speech" : "Talking Points"}
                          </span>
                          <Button variant="outline" onClick={() => copy(item.content)} data-testid={`history-speech-copy-${item.id}`} className="h-8 border-2 text-xs font-semibold">
                            <Copy className="h-3 w-3 mr-1" /> Copy
                          </Button>
                        </div>
                        <p className="mt-3 text-slate-800 whitespace-pre-wrap leading-relaxed font-mono text-sm">{item.content}</p>
                      </div>
                      <div className="flex justify-end pt-2">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" data-testid={`history-speech-delete-${item.id}`} className="h-9 text-flag-red hover:text-flag-red-hover hover:bg-red-50 font-semibold">
                              <Trash2 className="h-4 w-4 mr-1.5" /> Delete
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this speech?</AlertDialogTitle>
                              <AlertDialogDescription>It will be permanently removed from your history.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel data-testid={`history-speech-delete-cancel-${item.id}`}>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => removeSpeech(item.id)} data-testid={`history-speech-delete-confirm-${item.id}`} className="bg-flag-red hover:bg-flag-red-hover">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </TabsContent>
      </Tabs>
    </div>
  );
}
